<template>
  <div
    class="categories-list-item"
    :class="{
      'categories-list-item--active': isActive,
      'categories-list-item--for-collapsed': isCategoriesCollapsed
    }"
    @click="selectCategory()"
  >
    <div
      v-if="iconSvg"
      class="categories-list-item__icon"
      v-html="iconSvg"
    />

    <div class="categories-list-item__name">
      {{ category.attributes.name }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import DOMPurify from 'dompurify';

  import { getUploadUrl } from '@/helpers/urls';
  import { ref, onMounted } from 'vue';
  import { Category } from '@/services/category/api';

  const props = defineProps<{
    category: Category;
    isActive: boolean;
    isCategoriesCollapsed?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'select', categoryId: number): void;
  }>();

  const iconSvg = ref<string | null>(null);

  function selectCategory() {
    emit('select', props.category.id);
  }

  async function loadSvgInline() {
    if (!props.category.attributes.icon?.data?.attributes.url) {
      return;
    }

    const res = await fetch(getUploadUrl(props.category.attributes.icon?.data?.attributes.url));
    const rawSvg = await res.text();

    const cleanSvg = DOMPurify.sanitize(rawSvg, { USE_PROFILES: { svg: true } });

    iconSvg.value = cleanSvg;
  }

  onMounted(async () => {
    await loadSvgInline();
  });
</script>

<style>
  .categories-list-item {
    text-align: center;
    padding: 20px;
    border-radius: 16px;
    border: 2px solid var(--g-color-gray);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    color: #000;
    font-size: 18px;
    font-weight: 500;
    position: relative;
    overflow: hidden;
    gap: 16px;
    transition: all 0.1s ease-in-out;

    &__icon {
      width: 24px;
      height: 24px;
      object-fit: contain;
      color: #000;
      flex-shrink: 0;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: var(--g-color-orange);
      transform: translateX(-100%);
      z-index: -1;
    }

    &:hover {
      border-color: var(--g-color-orange);

      &::after {
        transform: translateX(0);
      }
    }

    &--active {
      background: var(--g-color-orange);
      color: #fff;
      border-color: var(--g-color-orange);
      box-shadow: 0 5px 15px rgba(var(--g-color-orange-rgb), 0.3);
      transform: scale(1.03);

      .categories-list-item__icon {
        color: #fff;
      }
    }

    &--for-collapsed {
      text-align: left;
      justify-content: flex-start;
      font-size: 18px;
    }

    &__name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  @media (--mobile) {
    .categories-list-item {
      padding: 15px;
      transform-origin: center center;

      &:active {
        transform: scale(0.98);
      }
    }
  }
</style>
